import { basePost } from '../../templates/Heritage/utils/api/base';
import type { RecordingResult, CompleteAssetResponse } from './tour-recording-types';
import { saveRecordedTour } from './my-tour-recordings-storage';

/**
 * Tải dữ liệu Blob lên URL đích bằng XMLHttpRequest để theo dõi tiến độ tải lên theo byte thời gian thực
 */
function uploadBlobWithXHR(
    url: string,
    blob: Blob,
    contentType: string,
    onProgress: (percent: number) => void
): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url, true);
        xhr.setRequestHeader('Content-Type', contentType);

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && event.total > 0) {
                const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
                onProgress(percent);
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`S3 upload failed with HTTP status ${xhr.status}`));
            }
        };

        xhr.onerror = () => {
            reject(new Error('Lỗi kết nối mạng khi tải video lên máy chủ S3'));
        };

        xhr.ontimeout = () => {
            reject(new Error('Hết thời gian chờ khi tải video lên máy chủ S3'));
        };

        xhr.send(blob);
    });
}

/**
 * Tải file video Blob và ảnh thumbnail lên S3 qua presigned URL và hoàn tất lưu asset với tiến độ thời gian thực
 */
export async function uploadTourRecording(
    result: RecordingResult,
    onProgress?: (percent: number, stepText?: string) => void
): Promise<CompleteAssetResponse | null> {
    const { blob, duration, tourName, idPath, fileSize, thumbnailBlob, recordingType = 'autotour' } = result;
    if (!idPath) {
        throw new Error('id_path không tồn tại, không thể tải lên video');
    }

    const notifyProgress = (percent: number, status: 'uploading' | 'completed' | 'error', message?: string) => {
        onProgress?.(percent, message);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(
                new CustomEvent('tour-recording-upload-progress', {
                    detail: { percent, status, message, tourName, recordingType }
                })
            );
        }
    };

    try {
        notifyProgress(5, 'uploading', 'Đang chuẩn bị và kết nối máy chủ...');

        // 1. Lấy Presigned PUT URL từ backend (cả video và thumbnail)
        const contentType = blob.type.split(';')[0] || 'video/mp4';
        const presignRes = await basePost<{
            success: boolean;
            data: {
                upload_url: string;
                s3_key: string;
                thumbnail_upload_url?: string;
                thumbnail_s3_key?: string;
                thumbnail_url?: string;
                recording_id: string;
                tour_name?: string;
                recording_type?: 'autotour' | 'ai_interaction';
                order?: number;
            };
        }>({
            endpoint: `/v1/projects/tour-recordings/presign?id_path=${encodeURIComponent(idPath)}`,
            body: {
                content_type: contentType,
                file_size: fileSize,
                tour_name: tourName,
                duration,
                recording_type: recordingType
            }
        });

        if (!presignRes.data?.success || !presignRes.data?.data?.upload_url) {
            const errorMsg = (presignRes.data as any)?.message || 'Không thể lấy URL tải lên S3 từ máy chủ';
            throw new Error(errorMsg);
        }

        const { upload_url, s3_key, thumbnail_upload_url, thumbnail_s3_key, thumbnail_url } = presignRes.data.data;
        notifyProgress(10, 'uploading', 'Bắt đầu truyền tải dữ liệu video...');

        // 2. Tải trực tiếp Blob lên CMC S3 (hỗ trợ retry 2 lần và realtime byte progress)
        let uploadSuccess = false;
        let lastError: any = null;

        for (let attempt = 1; attempt <= 2; attempt++) {
            try {
                if (typeof XMLHttpRequest !== 'undefined') {
                    await uploadBlobWithXHR(upload_url, blob, contentType, (bytePercent) => {
                        const mappedPercent = Math.min(85, Math.round(10 + (bytePercent * 0.75)));
                        notifyProgress(mappedPercent, 'uploading', `Đang tải video lên máy chủ (${bytePercent}%)...`);
                    });
                } else {
                    const putRes = await fetch(upload_url, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': contentType
                        },
                        body: blob
                    });
                    if (!putRes.ok) {
                        throw new Error(`S3 upload failed with HTTP status ${putRes.status}`);
                    }
                }

                uploadSuccess = true;
                break;
            } catch (err) {
                lastError = err;
                if (attempt < 2) {
                    notifyProgress(10, 'uploading', 'Đang thử lại kết nối tải lên video...');
                    await new Promise((res) => setTimeout(res, 2000));
                }
            }
        }

        if (!uploadSuccess) {
            throw lastError || new Error('Không thể tải video lên lưu trữ đám mây');
        }

        notifyProgress(86, 'uploading', 'Đang lưu ảnh đại diện (thumbnail)...');

        let hasUploadedThumbnail = false;
        // 2.1 Tải ảnh đại diện (thumbnail) lên S3 nếu có sẵn
        if (thumbnailBlob && thumbnail_upload_url) {
            try {
                const thumbRes = await fetch(thumbnail_upload_url, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'image/jpeg'
                    },
                    body: thumbnailBlob
                });
                if (thumbRes.ok) {
                    hasUploadedThumbnail = true;
                } else {
                    console.warn('[TourRecordingUploader] Tải thumbnail không thành công (HTTP ' + thumbRes.status + ')');
                }
            } catch (thumbUploadErr) {
                console.warn('[TourRecordingUploader] Không thể tải thumbnail trực tiếp:', thumbUploadErr);
            }
        }

        notifyProgress(92, 'uploading', 'Đang hoàn tất lưu trữ tài nguyên tour...');

        // 3. Gọi complete để lưu Asset vào DB dự án (chỉ gửi thumbnail nếu đã tải lên S3 thành công để backend fallback trích xuất nếu thiếu)
        const completeRes = await basePost<{
            success: boolean;
            data: CompleteAssetResponse;
        }>({
            endpoint: `/v1/projects/tour-recordings/complete?id_path=${encodeURIComponent(idPath)}`,
            body: {
                s3_key,
                thumbnail_url: hasUploadedThumbnail ? thumbnail_url : undefined,
                thumbnail_s3_key: hasUploadedThumbnail ? thumbnail_s3_key : undefined,
                tour_name: presignRes.data?.data?.tour_name || tourName,
                duration,
                file_size: fileSize,
                language_id: 'vn',
                recording_type: recordingType
            }
        });

        if (!completeRes.data?.success || !completeRes.data?.data) {
            const errorMsg = (completeRes.data as any)?.message || 'Lỗi khi lưu thông tin video tour vào danh mục tài nguyên';
            throw new Error(errorMsg);
        }

        const assetData = completeRes.data.data;
        notifyProgress(100, 'completed', 'Video đã sẵn sàng để xem lại hoặc chia sẻ');

        const resolvedTourName = presignRes.data?.data?.tour_name || tourName;
        const resolvedThumbnailUrl = (assetData as any).thumbnail_url || (hasUploadedThumbnail ? thumbnail_url : null);
        const categoryUuid = (assetData as any).category_uuid || '';
        const resolvedRecordingType = assetData.recording_type || recordingType;

        // Xây dựng share URL an toàn dẫn trực tiếp vào tab tư liệu video của dự án
        let finalShareUrl = assetData.share_url;
        if (!finalShareUrl || finalShareUrl.includes('/project/material')) {
            const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
            const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
            finalShareUrl = `${currentOrigin}${currentPath}?tab=nav_material&id=${assetData.asset_uuid}&type=video&source=recording&lang=vn`;
        } else if (finalShareUrl.startsWith('/') && typeof window !== 'undefined') {
            finalShareUrl = `${window.location.origin}${finalShareUrl}`;
        }

        // Lưu vào localStorage danh sách video cá nhân của khách
        try {
            saveRecordedTour(idPath, {
                uuid: assetData.asset_uuid,
                tourName: resolvedTourName,
                videoUrl: assetData.video_url,
                thumbnailUrl: resolvedThumbnailUrl,
                duration,
                fileSize,
                shareUrl: finalShareUrl,
                categoryUuid,
                recordingType: resolvedRecordingType
            });
        } catch (storageErr) {
            console.warn('[TourRecordingUploader] Không thể lưu vào session storage:', storageErr);
        }

        // Phát sự kiện toàn cục để mở Modal Kết Quả Tức Thì
        if (typeof window !== 'undefined') {
            window.dispatchEvent(
                new CustomEvent('tour-recording-completed', {
                    detail: {
                        tourName: resolvedTourName,
                        shareUrl: finalShareUrl,
                        videoUrl: assetData.video_url,
                        assetUuid: assetData.asset_uuid,
                        categoryUuid,
                        thumbnailUrl: resolvedThumbnailUrl,
                        duration,
                        fileSize,
                        idPath,
                        recordingType: resolvedRecordingType
                    }
                })
            );
        }

        return assetData;
    } catch (error: any) {
        notifyProgress(0, 'error', error?.message || 'Lỗi khi lưu video tour');
        throw error;
    }
}
